import type { ColumnType, TypedFormula } from "./formula";

/** One input of a function: its name as Excel's help names it, and its type. */
export interface FormulaParameter {
  name: string;
  type: ColumnType;
}

interface FormulaFunction {
  min: number;
  max: number;
  returns: (args: readonly TypedFormula[]) => ColumnType;
  /**
   * The inputs' types by position, the last repeating. An input is cast to its
   * type; one of a type that can never convert to it is a Diagnostic.
   */
  params?: readonly FormulaParameter[];
}

function argumentType(
  args: readonly TypedFormula[],
  index: number,
): ColumnType {
  return args[index]?.type ?? "text";
}
/** A fallback's result type: the value's own when both agree, else text, which holds either. */
function valueOrFallbackType(args: readonly TypedFormula[]): ColumnType {
  const value = argumentType(args, 0);
  return value === argumentType(args, 1) ? value : "text";
}

const text = (name: string): FormulaParameter => ({ name, type: "text" });
const number = (name: string): FormulaParameter => ({ name, type: "decimal" });
const date = (name: string): FormulaParameter => ({ name, type: "date" });
const logical: FormulaParameter = { name: "logical", type: "boolean" };

/**
 * The fixed function list, in the order it is presented to users. An error
 * value in any argument makes the call that error, as in a spreadsheet, except
 * in IF's branch not taken and in the error functions: IFERROR and IFNA replace
 * an error (any, or #N/A only) with a fallback, ISERROR and ISNA test for one.
 * A blank argument gives a blank result, as arithmetic does, except where a
 * function says otherwise (LEN, AND, OR, NOT).
 */
export const FORMULA_FUNCTIONS = {
  IF: { min: 3, max: 3, returns: (args) => argumentType(args, 1) },
  AND: { min: 1, max: 20, returns: () => "boolean", params: [logical] },
  OR: { min: 1, max: 20, returns: () => "boolean", params: [logical] },
  NOT: { min: 1, max: 1, returns: () => "boolean", params: [logical] },
  COALESCE: { min: 1, max: 20, returns: (args) => argumentType(args, 0) },
  CONCAT: { min: 1, max: 20, returns: () => "text" },
  UPPER: { min: 1, max: 1, returns: () => "text" },
  LOWER: { min: 1, max: 1, returns: () => "text" },
  TRIM: { min: 1, max: 1, returns: () => "text" },
  LEN: { min: 1, max: 1, returns: () => "decimal", params: [text("text")] },
  LEFT: {
    min: 1,
    max: 2,
    returns: () => "text",
    params: [text("text"), number("num_chars")],
  },
  MID: {
    min: 3,
    max: 3,
    returns: () => "text",
    params: [text("text"), number("start_num"), number("num_chars")],
  },
  RIGHT: {
    min: 1,
    max: 2,
    returns: () => "text",
    params: [text("text"), number("num_chars")],
  },
  VALUE: { min: 1, max: 1, returns: () => "decimal", params: [text("text")] },
  // The value's type depends on the format; see `textFormats`.
  TEXT: { min: 2, max: 2, returns: () => "text" },
  ROUND: { min: 2, max: 2, returns: () => "decimal" },
  ABS: { min: 1, max: 1, returns: () => "decimal" },
  DATE: {
    min: 3,
    max: 3,
    returns: () => "date",
    params: [number("year"), number("month"), number("day")],
  },
  TODAY: { min: 0, max: 0, returns: () => "date" },
  EDATE: {
    min: 2,
    max: 2,
    returns: () => "date",
    params: [date("start_date"), number("months")],
  },
  EOMONTH: {
    min: 2,
    max: 2,
    returns: () => "date",
    params: [date("start_date"), number("months")],
  },
  IFERROR: { min: 2, max: 2, returns: valueOrFallbackType },
  IFNA: { min: 2, max: 2, returns: valueOrFallbackType },
  ISERROR: { min: 1, max: 1, returns: () => "boolean" },
  ISNA: { min: 1, max: 1, returns: () => "boolean" },
} as const satisfies Record<string, FormulaFunction>;
export type FormulaFunctionName = keyof typeof FORMULA_FUNCTIONS;

/**
 * The lookup functions, exact match only. Their ranges are Table references to
 * another Source or Worksheet, so they are typed apart from `FORMULA_FUNCTIONS`.
 * XLOOKUP's match_mode and search_mode, and VLOOKUP's approximate match, parse
 * so the Plan can say they aren't supported.
 */
export const LOOKUP_FUNCTIONS = {
  XLOOKUP: { min: 3, max: 6 },
  VLOOKUP: { min: 3, max: 4 },
} as const satisfies Record<string, Omit<FormulaFunction, "returns">>;
export type LookupFunctionName = keyof typeof LOOKUP_FUNCTIONS;

/** How many criteria_range, criteria pairs SUMIFS and COUNTIFS take. */
export const MAX_CRITERIA_PAIRS = 8;

/**
 * The conditional aggregates, equality criteria only: SUMIFS(sum_range,
 * criteria_range1, criteria1, …) and COUNTIFS(criteria_range1, criteria1, …),
 * every range a column of one other Source or Worksheet.
 */
export const AGGREGATE_FUNCTIONS = {
  SUMIFS: { min: 3, max: 1 + 2 * MAX_CRITERIA_PAIRS },
  COUNTIFS: { min: 2, max: 2 * MAX_CRITERIA_PAIRS },
} as const satisfies Record<string, Omit<FormulaFunction, "returns">>;
export type AggregateFunctionName = keyof typeof AGGREGATE_FUNCTIONS;

/**
 * The functions whose inputs name another table's columns, where the Formula
 * editor offers those columns.
 */
export const TABLE_FUNCTIONS = { ...LOOKUP_FUNCTIONS, ...AGGREGATE_FUNCTIONS };

export type CallName =
  FormulaFunctionName | LookupFunctionName | AggregateFunctionName;

/** Every function name, in the order users are shown them. */
export const FUNCTION_NAMES: readonly CallName[] = [
  ...(Object.keys(FORMULA_FUNCTIONS) as FormulaFunctionName[]),
  ...(Object.keys(LOOKUP_FUNCTIONS) as LookupFunctionName[]),
  ...(Object.keys(AGGREGATE_FUNCTIONS) as AggregateFunctionName[]),
];

export function isFunctionName(name: string): name is FormulaFunctionName {
  return Object.hasOwn(FORMULA_FUNCTIONS, name);
}
export function isLookupFunctionName(name: string): name is LookupFunctionName {
  return Object.hasOwn(LOOKUP_FUNCTIONS, name);
}
export function isAggregateFunctionName(
  name: string,
): name is AggregateFunctionName {
  return Object.hasOwn(AGGREGATE_FUNCTIONS, name);
}

/** Whether `name` is a function, and `count` arguments are within its arity. */
export function isSupportedCall(name: string, count: number): name is CallName {
  const arity = isFunctionName(name)
    ? FORMULA_FUNCTIONS[name]
    : isLookupFunctionName(name)
      ? LOOKUP_FUNCTIONS[name]
      : isAggregateFunctionName(name)
        ? AGGREGATE_FUNCTIONS[name]
        : null;
  return arity !== null && count >= arity.min && count <= arity.max;
}

/** Declared types as users read them. */
export const TYPE_LABELS: Readonly<Record<ColumnType, string>> = {
  text: "Text",
  decimal: "Number",
  date: "Date",
  datetime: "Date and time",
  boolean: "TRUE/FALSE",
};

const NUMERIC: ReadonlySet<ColumnType> = new Set(["decimal", "boolean"]);
const DATES: ReadonlySet<ColumnType> = new Set(["date", "datetime"]);

/**
 * Whether the engine can cast a `from` value to `to`: text either way (text
 * that isn't the type is #VALUE!), a Number and TRUE/FALSE, a Date and a Date
 * and time. A Number is never a Date: there are no date serial numbers.
 */
export function converts(from: ColumnType, to: ColumnType): boolean {
  if (from === to || from === "text" || to === "text") return true;
  return (
    (NUMERIC.has(from) && NUMERIC.has(to)) || (DATES.has(from) && DATES.has(to))
  );
}

/** "EDATE's start_date must be a Date, not a Number." */
export function argumentTypeMessage(
  name: string,
  parameter: FormulaParameter,
  type: ColumnType,
): string {
  return `${name}'s ${parameter.name} must be ${parameter.type === "boolean" ? "TRUE or FALSE" : `a ${TYPE_LABELS[parameter.type]}`}, not ${type === "boolean" ? "TRUE/FALSE" : `a ${TYPE_LABELS[type]}`}.`;
}

/** The first argument of a call whose type can't convert to its parameter's, if any. */
export function mistypedArgument(
  name: FormulaFunctionName,
  args: readonly TypedFormula[],
): { index: number; parameter: FormulaParameter } | null {
  const function_: FormulaFunction = FORMULA_FUNCTIONS[name];
  const params = function_.params;
  if (!params?.length) return null;
  for (const [index, arg] of args.entries()) {
    const parameter = params[Math.min(index, params.length - 1)];
    if (parameter && !converts(arg.type, parameter.type))
      return { index, parameter };
  }
  return null;
}
